import { PRICING_MONTHLY_BASE_FEE_EUR, PRICING_PER_ALBARAN_EUR, formatEur } from '@/components/food-cost/constants'

export const VIAS_LANDING_VARIANT = 'vias-acompanamiento' as const

export type OfferPathId = 'tipi_express' | 'local_rentable' | 'apaga_incendio' | 'asesoria_waitlist'

export type RecommendedPlanId = Exclude<OfferPathId, 'asesoria_waitlist'>

type PaidOfferPath = {
    id: RecommendedPlanId
    label: string
    formLabel: string
    badge?: string
    forYouIf: string
    price: string
    /** Shown next to the price, e.g. "pago único", so a one-off fee is not read as monthly. */
    priceTag?: string
    priceNote: string
    /** What the visitor pays once the plan ends. */
    afterPlanNote?: string
    includes: string[]
    cta: string
}

const TOOL_FEE_NOTE = `Después, solo la cuota de Tipi: ${formatEur(PRICING_MONTHLY_BASE_FEE_EUR, 'es')}/mes + ${formatEur(PRICING_PER_ALBARAN_EUR, 'es')} por albarán`

export const OFFER_PATHS: PaidOfferPath[] = [
    {
        id: 'tipi_express',
        label: 'Tipi Express',
        formLabel: 'Tipi Express (1 mes)',
        forYouIf:
            'Quieres poner Tipi en marcha lo más rápido posible. Te acompañamos dentro de la herramienta, no es consultoría de negocio fuera de Tipi.',
        price: '600 €',
        priceTag: 'pago único',
        priceNote: '+ IVA · 1 mes · 4 sesiones de 1 h',
        afterPlanNote: TOOL_FEE_NOTE,
        includes: [
            'Análisis inicial con tus datos en Tipi',
            '4 sesiones para arrancar Tipi al ritmo más rápido',
            'Cierre del mes: cuánto te ha servido Tipi',
            '1 mes de app con 100 albaranes',
        ],
        cta: 'Quiero Tipi Express',
    },
    {
        id: 'local_rentable',
        label: 'Local Rentable',
        formLabel: 'Local Rentable (3 meses)',
        badge: 'Recomendado',
        forYouIf:
            'Quieres convertirte en mejor gestor: lo haces todo con Tipi, y en 3 meses tendrás el control de cada número de tu negocio y sabrás qué decisiones tomar en cada momento.',
        price: '1.500 €',
        priceTag: 'pago único',
        priceNote: '+ IVA · 3 meses · app incluida',
        includes: [
            'Pack de 3 meses con 300 albaranes',
            '12 sesiones de 1 h para usarlas cuando quieras',
            'Formación en gestión para controlar tu negocio',
        ],
        cta: 'Quiero Local Rentable',
    },
    {
        id: 'apaga_incendio',
        label: 'Apaga el incendio',
        formLabel: 'Apaga el incendio (en mi local)',
        forYouIf:
            'Facturas mucho pero pierdes miles por no tener control. No puedes esperar otra semana: vamos a tu local, volcamos escandallos y te lo dejamos montado y te enseñamos para que luego solo subas albaranes para tenerlo controlado.',
        price: 'A consultar',
        priceNote: 'Servicio puntual y presencial · de 2 a 3 días según tu negocio',
        includes: [
            'Equipo Tipi en persona en tu local',
            'Volcamos escandallos y te lo dejamos montado',
            'Después solo subes albaranes',
            'Formamos a tu equipo para saber usar Tipi',
            '1 mes de app con 100 albaranes',
        ],
        cta: 'Venid a apagar el incendio',
    },
]

export type RecommendationReason = {
    headline: string
    points: string[]
}

/** Why each plan fits, shown on the plan the /empieza quiz recommended. */
export const RECOMMENDED_PLAN_REASONS: Record<RecommendedPlanId, RecommendationReason> = {
    tipi_express: {
        headline: 'En marcha en un mes, y luego sigues solo',
        points: ['Ya sabes de gestión: solo te falta arrancar', 'Rentabilizas Tipi desde el primer mes'],
    },
    local_rentable: {
        headline: 'Aprende a ganar más con tu negocio',
        points: ['Un asesor que te enseña, no que lo hace por ti', 'Ganas más y el negocio depende menos de ti'],
    },
    apaga_incendio: {
        headline: 'Para la fuga sin parar tu día a día',
        points: [
            'Cada día sin orden es dinero perdido',
            'Lo montamos nosotros, en tu local',
            'Después tú solo lo mantienes',
        ],
    },
}

/** Replaces the plan's reason for visitors who answered they need to fix things urgently. */
export const RECOMMENDED_PLAN_URGENT_REASONS: Partial<Record<RecommendedPlanId, RecommendationReason>> = {
    tipi_express: {
        headline: 'Arranca ya, y luego sigues solo',
        points: ['En un mes lo tienes funcionando', 'Ves dónde se escapa el dinero desde la primera semana'],
    },
    local_rentable: {
        headline: 'Ordénalo ya, con alguien a tu lado',
        points: ['Ves dónde se escapa el dinero desde el primer mes', 'Un asesor te guía mientras lo arreglas'],
    },
}

/** Replaces the plan's reason for visitors who asked for someone to do it for them. */
export const RECOMMENDED_PLAN_DELEGATE_REASONS: Partial<Record<RecommendedPlanId, RecommendationReason>> = {
    local_rentable: {
        headline: 'Un asesor a tu lado, a tu ritmo',
        points: ['Te acompaña paso a paso durante 3 meses', '12 sesiones para usar cuando te vengan bien'],
    },
}

/** Query param the /empieza quiz sets to highlight the recommended plan. */
export const RECOMMENDED_PLAN_PARAM = 'plan'

export const RECOMMENDED_PLAN_BADGE = 'Recomendado para ti'

export function parseRecommendedPlan(value: string | null): RecommendedPlanId | null {
    return OFFER_PATHS.find(path => path.id === value)?.id ?? null
}

const ASESORIA_WAITLIST_FORM_OPTION = {
    id: 'asesoria_waitlist' as const,
    label: 'Asesoría Tipi (lista de espera)',
}

export const OFFER_PATH_FORM_OPTIONS: { id: OfferPathId; label: string }[] = [
    ...OFFER_PATHS.map(path => ({ id: path.id, label: path.formLabel })),
    ASESORIA_WAITLIST_FORM_OPTION,
]

const OFFER_PATH_LEAD_CONCERN: Record<OfferPathId, string> = {
    tipi_express: 'Plan: Tipi Express',
    local_rentable: 'Plan: Local Rentable',
    apaga_incendio: 'Plan: Apaga el incendio',
    asesoria_waitlist: 'Plan: Asesoría Tipi (lista de espera)',
}

export function getOfferPathLeadConcern(pathId: OfferPathId): string {
    return OFFER_PATH_LEAD_CONCERN[pathId]
}

/** Premium anchor shown on this page. CTA soft-gates to the form waitlist until `/asesoria` ships. */
export const ASESORIA_ANCHOR = {
    eyebrow: 'Solo 1 cliente al mes',
    label: 'Asesoría Tipi · Hostelería rentable',
    forYouIf:
        'Tu negocio ya factura bien y no quieres un administrativo al que enseñar el sector. Quieres a alguien que sepa de números y de hostelería: que no solo te diga qué porcentaje está alto, sino qué lo provoca y qué hacer.',
    capacityNote: 'Priorizamos un servicio excelente. Si este mes ya está cogido, te avisamos para el siguiente.',
    faces: [
        {
            src: '/asesoria/hero.jpg',
            alt: 'Samuel Carrillo',
            name: 'Samuel',
        },
        {
            src: '/asesoria/javi.jpg',
            alt: 'Javier Porras',
            name: 'Javi',
        },
    ],
    price: '14.000 €',
    priceVariable: '+ 10 %',
    priceVariableLabel: 'de la mejora de beneficio que generemos juntos',
    priceNote: 'Plan anual · + IVA · app incluida · hasta 2 locales',
    includes: [
        'Facturas y ventas preparadas para tu gestoría el día 1 de cada mes',
        'Reclamamos a tus proveedores las facturas que falten',
        'Negociamos con cualquiera de tus proveedores, con precios especiales',
        'App Tipi, red de contactos del sector y búsqueda de financiación para nuevos proyectos',
        'Visitas presenciales trimestrales (alojamiento y transporte aparte)',
    ],
    cta: 'Entrar en lista de espera',
    offerPathId: 'asesoria_waitlist' as const,
} as const

export const TOOL_ONLY_EXIT = {
    lead: '¿No necesitas acompañamiento?',
    body: 'Contrata ya la herramienta y ponle solución por ti mismo.',
    cta: 'Ir a Tipi herramienta',
} as const
